import { NavLink } from 'react-router-dom';
import { navLinks, contactDetails, socialLinks } from '../data/siteContent';

const socialIconMap = {
  Facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M22 12a10 10 0 1 0-11.56 9.92v-7h-2.4V12h2.4v-2.2c0-2.37 1.42-3.68 3.58-3.68 1.04 0 2.14.19 2.14.19v2.35h-1.21c-1.2 0-1.57.75-1.57 1.52V12h2.67l-.43 2.92h-2.24v7A10 10 0 0 0 22 12Z" />
    </svg>
  ),
  Instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9A4.5 4.5 0 0 1 16.5 21h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3Zm0 2A2.5 2.5 0 0 0 5 7.5v9A2.5 2.5 0 0 0 7.5 19h9a2.5 2.5 0 0 0 2.5-2.5v-9A2.5 2.5 0 0 0 16.5 5h-9Z" />
      <path d="M12 8.1a3.9 3.9 0 1 1 0 7.8 3.9 3.9 0 0 1 0-7.8Zm0 2a1.9 1.9 0 1 0 0 3.8 1.9 1.9 0 0 0 0-3.8Z" />
      <path d="M17.4 7.05a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0Z" />
    </svg>
  ),
  LinkedIn: (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M4.98 3.5a2.5 2.5 0 1 1-4.98 0 2.5 2.5 0 0 1 4.98 0Z" />
      <path d="M.5 8.5h4.9V22H.5zM9.5 8.5h4.7v1.9h.07c.65-1.23 2.24-2.52 4.6-2.52 3.04 0 4.63 2.18 4.63 6.25V22H18.6v-6.9c0-1.64-.03-3.76-2.29-3.76-2.3 0-2.65 1.72-2.65 3.64V22H9.5z" />
    </svg>
  ),
  YouTube: (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.8 8.01a2.75 2.75 0 0 0-1.94-1.95C18.25 5.75 12 5.75 12 5.75s-6.25 0-7.86.31A2.75 2.75 0 0 0 2.2 8C2 9.65 2 12 2 12s0 2.35.2 3.99a2.75 2.75 0 0 0 1.94 1.95c1.61.31 7.86.31 7.86.31s6.25 0 7.86-.31a2.75 2.75 0 0 0 1.94-1.95C22 14.35 22 12 22 12s0-2.35-.2-3.99ZM10 15.5v-7l6 3.5-6 3.5Z"
      />
    </svg>
  ),
  TikTok: (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
      <path d="M21.55 7.2a4.66 4.66 0 0 1-2.76-.91 5.3 5.3 0 0 1-1.22-1.38v8.36a5.48 5.48 0 1 1-4.47-5.4v3.08a2.2 2.2 0 1 0 1.61 2.12V2h3.14a4.9 4.9 0 0 0 1.47 3.38 4.9 4.9 0 0 0 2.23 1.28Z" />
    </svg>
  ),
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-24 bg-background text-white/70">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-16 md:grid-cols-3">
        <div>
          <img src="/logo-light.png" alt="Duoley logo" className="h-10 w-auto" />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/70">
            Farm-fresh dairy and juice products celebrating Sidama’s heritage, sustainability, and community.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {socialLinks.map(({ label, url }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white/80 transition-all hover:border-secondary hover:bg-secondary/20 hover:text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60"
                aria-label={`Duoley on ${label}`}
              >
                {socialIconMap[label] ?? <span className="text-sm font-semibold">{label[0]}</span>}
              </a>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-[0.25em] text-white">Explore</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {navLinks.map(({ label, path }) => (
              <li key={path}>
                <NavLink to={path} className="text-white/70 transition-colors hover:text-secondary">
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-display text-sm font-semibold uppercase tracking-[0.25em] text-white">Connect</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li>
              <a href={`mailto:${contactDetails.email}`} className="text-white/70 transition-colors hover:text-secondary">
                {contactDetails.email}
              </a>
            </li>
            <li>
              <a href={`tel:${contactDetails.phone.replace(/\s/g, '')}`} className="text-white/70 transition-colors hover:text-secondary">
                {contactDetails.phone}
              </a>
            </li>
            <li className="text-white/70">{contactDetails.address}</li>
            <li className="text-white/70">{contactDetails.officeHours}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
        <p>© {currentYear} Duoley Food Processing PLC. All rights reserved.</p>
        <p className="mt-1">
          Built by{' '}
          <a
            href="https://infnova.tech/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium transition-opacity hover:opacity-80"
            style={{ color: '#fb8c00' }}
          >
            INFNOVA Technologies
          </a>
        </p>
      </div>
    </footer>
  );
}
